import { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock, faUser, faXmark } from "@fortawesome/free-solid-svg-icons";
import { AuthContext } from "../../context/AuthContext";
import Button from "../Button/Button";
import EditUserDetails from "./EditUserDetails";
import PasswordUpdateModal from "./PasswordUpdateModal";
import styles from "./ProfileEdit.module.css";

function ProfileEdit({ isVisible, handleModal }) {
  const { user } = useContext(AuthContext);
  const userId = user?.id;
  const [view, setView] = useState("profile");
  const [saving, setSaving] = useState(false);
  const dialogRef = useRef(null);
  const closeRef = useRef(handleModal);
  const savingRef = useRef(saving);
  closeRef.current = handleModal;
  savingRef.current = saving;

  useEffect(() => {
    if (!isVisible || !userId) return undefined;
    setView("profile");
    const previousFocus = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.querySelector("button")?.focus();
    const keydown = (event) => {
      const dialogs = document.querySelectorAll('[role="dialog"]');
      if (dialogs[dialogs.length - 1] !== dialogRef.current) return;
      if (event.key === "Escape" && !savingRef.current) closeRef.current(false);
      if (event.key !== "Tab") return;
      const elements = [...dialogRef.current.querySelectorAll('button:not(:disabled), input:not(:disabled):not([type="file"]), textarea:not(:disabled), [tabindex="0"]')]
        .filter((element) => element.getClientRects().length);
      const first = elements[0]; const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener("keydown", keydown);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener("keydown", keydown);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [isVisible, userId]);

  if (!isVisible || !user) return null;
  const close = () => { if (!saving) handleModal(false); };

  return createPortal(
    <div className={styles.modalBackdrop} onMouseDown={(event) => event.target === event.currentTarget && close()}>
      <section ref={dialogRef} className={styles.modalContent} role="dialog" aria-modal="true" aria-labelledby="profile-edit-title">
        <header className={styles.header}>
          <div><p className={styles.eyebrow}>Minha conta</p><h2 id="profile-edit-title">Editar perfil</h2><p className={styles.description}>Cuide dos seus dados e da segurança da sua conta.</p></div>
          <Button variant="icon" type="button" onClick={close} disabled={saving} aria-label="Fechar edição de perfil"><FontAwesomeIcon icon={faXmark} /></Button>
        </header>
        <div className={styles.tabs} aria-label="Opções da conta">
          <button type="button" className={view === "profile" ? styles.activeTab : ""} aria-pressed={view === "profile"} onClick={() => setView("profile")} disabled={saving}><FontAwesomeIcon icon={faUser} /> Perfil</button>
          <button type="button" className={view === "password" ? styles.activeTab : ""} aria-pressed={view === "password"} onClick={() => setView("password")} disabled={saving}><FontAwesomeIcon icon={faLock} /> Senha</button>
        </div>
        {view === "profile"
          ? <EditUserDetails isVisible handleModal={handleModal} username={user.username} onBusyChange={setSaving} />
          : <PasswordUpdateModal isVisible handleModal={handleModal} username={user.username} onBusyChange={setSaving} />}
      </section>
    </div>,
    document.body
  );
}
export default ProfileEdit;
